/**
 * A table whose headers span columns or rows, which `DataTable`'s one header
 * row per column cannot draw: a timetable, a week grid, a matrix.
 *
 * The caller writes the `thead` and `tbody`; this owns the scroller, the
 * border and daisyUI's table classes, so no screen spells them by hand. Reach
 * for `DataTable` first.
 *
 * @module components/ui/Table
 */

import type { ReactNode } from "react";

export const TABLE_DENSITIES = ["xs", "sm", "md"] as const;
export type TableDensity = (typeof TABLE_DENSITIES)[number];

const DENSITY_CLASS: Record<TableDensity, string> = {
  xs: "table-xs",
  sm: "table-sm",
  md: "",
};

export interface TableProps {
  caption: string;
  showCaption?: boolean;
  density?: TableDensity;
  /** Alternate row fills, for a wide row the eye has to follow across. */
  zebra?: boolean;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * <Table caption="Assessment timeline" density="xs">
 *   <thead>…</thead>
 *   <tbody>…</tbody>
 * </Table>
 */
export function Table({
  caption,
  showCaption = false,
  density = "sm",
  zebra = false,
  children,
  className = "",
  "data-testid": testId = undefined,
}: TableProps) {
  return (
    <div
      className={[
        // relative: an sr-only caption is absolutely positioned and would
        // otherwise escape the scroller, as it did in DataTable.
        "relative overflow-x-auto rounded-box border border-base-300 bg-base-100",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <table
        className={["table", DENSITY_CLASS[density], zebra ? "table-zebra" : ""]
          .filter(Boolean)
          .join(" ")}
        data-testid={testId}
      >
        <caption className={showCaption ? "px-4 py-3 text-left text-sm text-muted" : "sr-only"}>
          {caption}
        </caption>
        {children}
      </table>
    </div>
  );
}
